import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Modal, Table } from 'antd';
import GroupInfoDrawer from '../components/GroupInfoDrawer';
import ReservationManagement from './ReservationManagement';
import { getOrderStatusList, getGroupSettlementMain, getOutstandingReceivables, getReservationTravelerListBySaleNo } from '../services/apiService';

const SettlementSystem = () => {
  const navigate = useNavigate();
  const getDefaultSearchState = () => {
    const today = new Date();
    const oneMonthLater = new Date(today);
    oneMonthLater.setMonth(today.getMonth() + 1);
    
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    return {
      period: 'custom',
      selectedMonth: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      selectedQuarter: `${today.getFullYear()}-Q${Math.floor(today.getMonth() / 3) + 1}`,
      selectedYear: String(today.getFullYear()),
      manualStartDate: formatDate(today),
      manualEndDate: formatDate(oneMonthLater),
      orderSearchBasis: '2',
    };
  };
  const [searchStateByTab, setSearchStateByTab] = useState(() => ({
    orders: getDefaultSearchState(),
    group: getDefaultSearchState(),
    product: getDefaultSearchState(),
    stats: getDefaultSearchState(),
  }));

  // data states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]); // live list from API (예약현황)
  // filters
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | '완료' | '부분' | '기타'
  const [keyword, setKeyword] = useState(''); // search in 상품명, 예약고객명
  const [groupNoFilter, setGroupNoFilter] = useState(''); // 행사단체번호 exact or substring
  const [groupNameFilter, setGroupNameFilter] = useState(''); // 행사단체명 substring
  const [reservationStatusFilter, setReservationStatusFilter] = useState('all'); // 'all' | '확정' | '취소' | '기타'
  const [showCancelledOrders, setShowCancelledOrders] = useState(false); // 취소주문 보기
  
  // Group drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerSaleNo, setDrawerSaleNo] = useState(null);
  const [drawerTitle, setDrawerTitle] = useState('');
  // Settlement (group) dataset for product-wise analysis
  const [settleRows, setSettleRows] = useState([]);
  const [settleLoading, setSettleLoading] = useState(false);
  const [settleError, setSettleError] = useState(null);
  const [outstandingRows, setOutstandingRows] = useState([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [outstandingError, setOutstandingError] = useState(null);
  const [outstandingModalOpen, setOutstandingModalOpen] = useState(false);
  const [preDepositModalOpen, setPreDepositModalOpen] = useState(false);
  const [orderSortKey, setOrderSortKey] = useState(null);
  const [orderSortDir, setOrderSortDir] = useState('asc');
  const [openOrderHeaderFilterKey, setOpenOrderHeaderFilterKey] = useState(null);
  const [orderHeaderFilterSearch, setOrderHeaderFilterSearch] = useState({});
  const [orderHeaderFilterSelected, setOrderHeaderFilterSelected] = useState({});
  const orderHeaderFilterRootRef = useRef(null);
  const orderHeaderFilterAnchorElRef = useRef(null);
  const [orderHeaderFilterAnchorRect, setOrderHeaderFilterAnchorRect] = useState(null);
  const [groupSortKey, setGroupSortKey] = useState(null);
  const [groupSortDir, setGroupSortDir] = useState('asc');
  const [openGroupHeaderFilterKey, setOpenGroupHeaderFilterKey] = useState(null);
  const [groupHeaderFilterSearch, setGroupHeaderFilterSearch] = useState({});
  const [groupHeaderFilterSelected, setGroupHeaderFilterSelected] = useState({});
  const groupHeaderFilterRootRef = useRef(null);
  const groupHeaderFilterAnchorElRef = useRef(null);
  const [groupHeaderFilterAnchorRect, setGroupHeaderFilterAnchorRect] = useState(null);

  const travelerHoverTimerRef = useRef(null);
  const travelerCloseTimerRef = useRef(null);
  const travelerRequestIdRef = useRef(0);
  const travelerCacheRef = useRef(new Map());
  const travelerPendingRef = useRef({ saleNo: null, anchorRect: null });
  const travelerPopoverRootRef = useRef(null);
  const [travelerPopover, setTravelerPopover] = useState({ open: false, saleNo: null, anchorRect: null, loading: false, error: null, list: [] });
  // UI states for cleaner layout
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('reservationSearch'); // 'group' | 'orders'
  const activeSearchState = useMemo(() => {
    if (activeTab && searchStateByTab?.[activeTab]) return searchStateByTab[activeTab];
    if (searchStateByTab?.orders) return searchStateByTab.orders;
    return getDefaultSearchState();
  }, [activeTab, searchStateByTab]);

  // Helper function to determine which quick selection period is active
  const getActiveQuickSelection = () => {
    const { manualStartDate, manualEndDate } = activeSearchState || {};
    if (!manualStartDate || !manualEndDate) return null;

    const formatLocalDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    // manualStartDate/manualEndDate are already stored as YYYY-MM-DD strings.
    // Avoid using toISOString() (UTC) because it can shift dates in KST and break active button detection.
    const today = new Date();
    const todayStr = formatLocalDate(today);
    const startStr = manualStartDate;
    const endStr = manualEndDate;
    
    // Check for "오늘"
    if (startStr === todayStr && endStr === todayStr) return 'today';
    
    // Check for "일주일"
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const weekLater = new Date(today);
    weekLater.setDate(today.getDate() + 7);
    const weekAgoStr = formatLocalDate(weekAgo);
    const weekLaterStr = formatLocalDate(weekLater);
    
    if ((activeSearchState?.orderSearchBasis === '1' && startStr === weekAgoStr && endStr === todayStr) ||
        (activeSearchState?.orderSearchBasis === '2' && startStr === todayStr && endStr === weekLaterStr) ||
        (startStr === todayStr && endStr === weekLaterStr)) {
      return 'week';
    }
    
    // Check for "한달"
    const monthAgo = new Date(today);
    monthAgo.setMonth(today.getMonth() - 1);
    const monthLater = new Date(today);
    monthLater.setMonth(today.getMonth() + 1);
    const monthAgoStr = formatLocalDate(monthAgo);
    const monthLaterStr = formatLocalDate(monthLater);
    
    if ((activeSearchState?.orderSearchBasis === '1' && startStr === monthAgoStr && endStr === todayStr) ||
        (activeSearchState?.orderSearchBasis === '2' && startStr === todayStr && endStr === monthLaterStr) ||
        (startStr === todayStr && endStr === monthLaterStr)) {
      return 'month';
    }
    
    // Check for "3개월"
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    const threeMonthsLater = new Date(today);
    threeMonthsLater.setMonth(today.getMonth() + 3);
    const threeMonthsAgoStr = formatLocalDate(threeMonthsAgo);
    const threeMonthsLaterStr = formatLocalDate(threeMonthsLater);
    
    if ((activeSearchState?.orderSearchBasis === '1' && startStr === threeMonthsAgoStr && endStr === todayStr) ||
        (activeSearchState?.orderSearchBasis === '2' && startStr === todayStr && endStr === threeMonthsLaterStr) ||
        (startStr === todayStr && endStr === threeMonthsLaterStr)) {
      return 'threeMonths';
    }
    
    return null;
  };

  const handleSearchBasisChange = (newBasis) => {
    const currentPeriod = activeSearchState?.period;
    const currentStartDate = activeSearchState?.manualStartDate;
    const currentEndDate = activeSearchState?.manualEndDate;
    
    // If currently using a custom period with specific dates, adjust them based on the new basis
    if (currentPeriod === 'custom' && currentStartDate && currentEndDate) {
      const today = new Date();
      const formatDate = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
      };
      
      // Calculate the period difference between current dates
      const start = new Date(currentStartDate);
      const end = new Date(currentEndDate);
      const daysDiff = Math.round((end - start) / (1000 * 60 * 60 * 24));
      
      if (newBasis === '1') {
        // 예약일 기준: 종료일을 오늘로, 시작일은 기간 유지
        const newStart = new Date(today);
        newStart.setDate(today.getDate() - daysDiff);
        updateActiveSearchState({ 
          orderSearchBasis: newBasis,
          manualStartDate: formatDate(newStart),
          manualEndDate: formatDate(today)
        });
      } else if (newBasis === '2') {
        // 출발일 기준: 시작일을 오늘로, 종료일은 기간 유지
        const newEnd = new Date(today);
        newEnd.setDate(today.getDate() + daysDiff);
        updateActiveSearchState({ 
          orderSearchBasis: newBasis,
          manualStartDate: formatDate(today),
          manualEndDate: formatDate(newEnd)
        });
      }
    } else {
      // 기간이 설정되지 않았거나 다른 기간 유형이면 기본값으로 설정
      updateActiveSearchState({ orderSearchBasis: newBasis });
    }
  };

  const updateActiveSearchState = (partial) => {
    const tabKey = activeTab;
    if (!tabKey || !searchStateByTab?.[tabKey]) return;
    setSearchStateByTab((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev?.[tabKey] || getDefaultSearchState()),
        ...partial,
      },
    }));
  };

  // quick period helpers
  const setToToday = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    const todayStr = `${y}-${m}-${d}`;
    
    // 예약일 기준 조회인 경우 종료일을 오늘로 설정
    if (activeSearchState?.orderSearchBasis === '1') {
      updateActiveSearchState({ period: 'custom', manualStartDate: todayStr, manualEndDate: todayStr });
    } else {
      updateActiveSearchState({ period: 'custom', manualStartDate: todayStr, manualEndDate: todayStr });
    }
  };

  const setToOneWeek = () => {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    // 예약일 기준 조회인 경우 종료일을 오늘로, 시작일을 7일 전으로 설정
    if (activeSearchState?.orderSearchBasis === '1') {
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(weekAgo), 
        manualEndDate: formatDate(today) 
      });
    } else {
      const weekLater = new Date(today);
      weekLater.setDate(today.getDate() + 7);
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(today), 
        manualEndDate: formatDate(weekLater) 
      });
    }
  };

  const setToOneMonth = () => {
    const today = new Date();
    const monthAgo = new Date(today);
    monthAgo.setMonth(today.getMonth() - 1);
    
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    // 예약일 기준 조회인 경우 종료일을 오늘로, 시작일을 1개월 전으로 설정
    if (activeSearchState?.orderSearchBasis === '1') {
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(monthAgo), 
        manualEndDate: formatDate(today) 
      });
    } else {
      const monthLater = new Date(today);
      monthLater.setMonth(today.getMonth() + 1);
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(today), 
        manualEndDate: formatDate(monthLater) 
      });
    }
  };

  const setToThreeMonths = () => {
    const today = new Date();
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };
    
    // 예약일 기준 조회인 경우 종료일을 오늘로, 시작일을 3개월 전으로 설정
    if (activeSearchState?.orderSearchBasis === '1') {
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(threeMonthsAgo), 
        manualEndDate: formatDate(today) 
      });
    } else {
      const threeMonthsLater = new Date(today);
      threeMonthsLater.setMonth(today.getMonth() + 3);
      updateActiveSearchState({ 
        period: 'custom', 
        manualStartDate: formatDate(today), 
        manualEndDate: formatDate(threeMonthsLater) 
      });
    }
  };
  const clearFilters = () => {
    setStatusFilter('all');
    setKeyword('');
    setGroupNoFilter('');
    setGroupNameFilter('');
    setReservationStatusFilter('all');
  };

  const getMouseAnchorRect = (e) => {
    const x = typeof e?.clientX === 'number' ? e.clientX : (typeof e?.nativeEvent?.clientX === 'number' ? e.nativeEvent.clientX : null);
    const y = typeof e?.clientY === 'number' ? e.clientY : (typeof e?.nativeEvent?.clientY === 'number' ? e.nativeEvent.clientY : null);
    if (x === null || y === null) return null;
    return { left: x, right: x, top: y, bottom: y, width: 0, height: 0 };
  };

  const closeTravelerPopover = () => {
    setTravelerPopover((prev) => ({ ...prev, open: false }));
  };

  const formatTravelerDate = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const s = String(value).trim();
    if (!s) return '';
    if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
    if (s.includes('T')) return s.split('T')[0];
    if (s.includes(' ')) return s.split(' ')[0];
    return s;
  };

  const openTravelerPopover = async ({ saleNo, anchorRect }) => {
    if (!saleNo || !anchorRect) return;
    if (travelerCloseTimerRef.current) {
      clearTimeout(travelerCloseTimerRef.current);
      travelerCloseTimerRef.current = null;
    }

    const cached = travelerCacheRef.current.get(String(saleNo));
    if (Array.isArray(cached)) {
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: null, list: cached });
      return;
    }

    const requestId = ++travelerRequestIdRef.current;
    setTravelerPopover({ open: true, saleNo, anchorRect, loading: true, error: null, list: [] });
    try {
      const list = await getReservationTravelerListBySaleNo({ saleNo });
      if (requestId !== travelerRequestIdRef.current) return;
      if (!travelerPendingRef.current || String(travelerPendingRef.current.saleNo) !== String(saleNo)) return;
      const normalized = Array.isArray(list) ? list : [];
      travelerCacheRef.current.set(String(saleNo), normalized);
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: null, list: normalized });
    } catch (e) {
      if (requestId !== travelerRequestIdRef.current) return;
      setTravelerPopover({ open: true, saleNo, anchorRect, loading: false, error: '여행객 정보를 불러오지 못했습니다.', list: [] });
    }
  };

  const handleOrderRowMouseEnter = (e, row) => {
    const s = row?.판매번호 ?? row?.주문판매번호 ?? row?.대표판매번호 ?? row?.대표주문번호 ?? null;
    if (!s) return;
    if (travelerCloseTimerRef.current) {
      clearTimeout(travelerCloseTimerRef.current);
      travelerCloseTimerRef.current = null;
    }
    if (travelerHoverTimerRef.current) clearTimeout(travelerHoverTimerRef.current);
    travelerPendingRef.current = { saleNo: s, anchorRect: getMouseAnchorRect(e) };
    travelerHoverTimerRef.current = setTimeout(() => {
      openTravelerPopover(travelerPendingRef.current);
    }, 500);
  };

  const handleOrderRowMouseMove = (e, row) => {
    if (!travelerHoverTimerRef.current) return;
    const s = row?.판매번호 ?? row?.주문판매번호 ?? row?.대표판매번호 ?? row?.대표주문번호 ?? null;
    if (!s) return;
    if (!travelerPendingRef.current || String(travelerPendingRef.current.saleNo) !== String(s)) return;
    const nextRect = getMouseAnchorRect(e);
    if (!nextRect) return;
    travelerPendingRef.current = { ...travelerPendingRef.current, anchorRect: nextRect };
  };

  const handleOrderRowMouseLeave = () => {
    if (travelerHoverTimerRef.current) {
      clearTimeout(travelerHoverTimerRef.current);
      travelerHoverTimerRef.current = null;
    }
    if (travelerCloseTimerRef.current) clearTimeout(travelerCloseTimerRef.current);
    travelerCloseTimerRef.current = setTimeout(() => {
      closeTravelerPopover();
    }, 120);
  };

  useEffect(() => {
    if (!travelerPopover.open) return;
    const onScroll = (ev) => {
      try {
        if (travelerPopoverRootRef.current && ev?.target && travelerPopoverRootRef.current.contains(ev.target)) return;
      } catch (_) {}
      closeTravelerPopover();
    };
    const onResize = () => closeTravelerPopover();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [travelerPopover.open]);

  const toggleOrderSort = (key) => {
    setOrderSortDir((prevDir) => {
      if (orderSortKey !== key) return 'asc';
      return prevDir === 'asc' ? 'desc' : 'asc';
    });
    setOrderSortKey(key);
  };

  const toggleGroupSort = (key) => {
    setGroupSortDir((prevDir) => {
      if (groupSortKey !== key) return 'asc';
      return prevDir === 'asc' ? 'desc' : 'asc';
    });
    setGroupSortKey(key);
  };

  useEffect(() => {
    if (!openOrderHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = orderHeaderFilterRootRef.current;
      const anchor = orderHeaderFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenOrderHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openOrderHeaderFilterKey]);

  useEffect(() => {
    if (!openGroupHeaderFilterKey) return;
    const onMouseDown = (e) => {
      const pop = groupHeaderFilterRootRef.current;
      const anchor = groupHeaderFilterAnchorElRef.current;
      if (pop && pop.contains(e.target)) return;
      if (anchor && anchor.contains(e.target)) return;
      setOpenGroupHeaderFilterKey(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openGroupHeaderFilterKey]);

  useEffect(() => {
    if (!openGroupHeaderFilterKey) return;
    const updateRect = () => {
      const el = groupHeaderFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setGroupHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openGroupHeaderFilterKey]);

  useEffect(() => {
    if (!openOrderHeaderFilterKey) return;
    const updateRect = () => {
      const el = orderHeaderFilterAnchorElRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setOrderHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [openOrderHeaderFilterKey]);

  // derive start/end date from current period selection
  const dateRange = useMemo(() => {
    const {
      period,
      selectedMonth,
      selectedQuarter,
      selectedYear,
      manualStartDate,
      manualEndDate,
    } = activeSearchState || {};
    const pad = (n) => String(n).padStart(2, '0');
    const toYmd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
    if (period === 'custom') {
      if (manualStartDate && manualEndDate) {
        const a = new Date(manualStartDate);
        const b = new Date(manualEndDate);
        if (!isNaN(a.getTime()) && !isNaN(b.getTime())) {
          const start = a <= b ? manualStartDate : manualEndDate;
          const end = a <= b ? manualEndDate : manualStartDate;
          return { start, end };
        }
      }
      return null;
    }
    if (period === 'monthly') {
      const [yStr, mStr] = (selectedMonth || '').split('-');
      const y = Number(yStr);
      const m = Number(mStr);
      if (!y || !m) return null;
      const lastDay = new Date(y, m, 0).getDate(); // month is 1..12
      return { start: toYmd(y, m, 1), end: toYmd(y, m, lastDay) };
    }
    if (period === 'quarterly') {
      const [yStr, qStr] = (selectedQuarter || '0-Q1').split('-Q');
      const y = Number(yStr);
      const q = Number(qStr);
      if (!y || !q) return null;
      const startMonth = (q - 1) * 3 + 1;
      const endMonth = startMonth + 2;
      const lastDay = new Date(y, endMonth, 0).getDate();
      return { start: toYmd(y, startMonth, 1), end: toYmd(y, endMonth, lastDay) };
    }
    if (period === 'yearly') {
      const y = Number(selectedYear);
      if (!y) return null;
      return { start: `${y}-01-01`, end: `${y}-12-31` };
    }
    return null;
  }, [
    activeSearchState?.period,
    activeSearchState?.selectedMonth,
    activeSearchState?.selectedQuarter,
    activeSearchState?.selectedYear,
    activeSearchState?.manualStartDate,
    activeSearchState?.manualEndDate,
  ]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const startDate = dateRange?.start;
      const endDate = dateRange?.end;
      const orderSearchBasis = activeSearchState?.orderSearchBasis;
      const [list, groups, outstanding] = await Promise.all([
        getOrderStatusList({ startDate, endDate, searchBasis: orderSearchBasis }),
        (async () => {
          try {
            setSettleLoading(true);
            setSettleError(null);
            const g = await getGroupSettlementMain({ startDate, endDate, searchBasis: '행사일', manager: 'NULL' });
            return Array.isArray(g) ? g : [];
          } catch (e) {
            console.error('Failed to fetch group settlement main', e);
            setSettleError(e?.message || '단체 결산 데이터를 불러오지 못했습니다.');
            return [];
          } finally {
            setSettleLoading(false);
          }
        })(),
        (async () => {
          try {
            setOutstandingLoading(true);
            setOutstandingError(null);
            const r = await getOutstandingReceivables({ startDate, endDate });
            return Array.isArray(r) ? r : [];
          } catch (e) {
            console.error('Failed to fetch outstanding receivables', e);
            setOutstandingError(e?.message || '미수 현황을 불러오지 못했습니다.');
            return [];
          } finally {
            setOutstandingLoading(false);
          }
        })(),
      ]);
      setRows(Array.isArray(list) ? list : []);
      setSettleRows(groups);
      const filteredOutstanding = Array.isArray(outstanding)
        ? outstanding.filter((r) => String(r?.입금상태 || '') === '미수')
        : [];
      setOutstandingRows(filteredOutstanding);
    } catch (e) {
      console.error('Failed to fetch settlement list', e);
      setError(e?.message || '정산 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // initial and whenever selection changes
  useEffect(() => {
    loadData();
  }, [
    activeTab,
    activeSearchState?.period,
    activeSearchState?.selectedMonth,
    activeSearchState?.selectedQuarter,
    activeSearchState?.selectedYear,
    activeSearchState?.manualStartDate,
    activeSearchState?.manualEndDate,
    activeSearchState?.orderSearchBasis,
  ]);

  // helpers
  const parseNumber = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const num = Number(String(v).replace(/,/g, ''));
    return Number.isFinite(num) ? num : 0;
  };
  const formatCurrency = (amount) => new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(amount || 0);
  const formatDateOnly = (v) => {
    const s = String(v || '').trim();
    if (!s) return '';
    if (s.includes('T')) return s.split('T')[0];
    if (s.includes(' ')) return s.split(' ')[0];
    return s;
  };
  const parseDateTime = (v) => {
    if (!v) return null;
    const s = String(v).trim();
    if (!s) return null;
    // normalize "YYYY-MM-DD HH:mm" -> "YYYY-MM-DDTHH:mm"
    const norm = s.includes('T') ? s : s.replace(' ', 'T');
    const d = new Date(norm);
    return isNaN(d.getTime()) ? null : d;
  };

  // Build a map of groupNo -> { minStart: Date, minStartStr: string, maxEnd: Date, maxEndStr: string }
  const groupDatesByGroupNo = useMemo(() => {
    const map = new Map();
    const arr = Array.isArray(rows) ? rows : [];
    for (const r of arr) {
      const gno = r?.행사단체번호;
      if (gno === undefined || gno === null || String(gno) === '') continue;
      const key = String(gno);
      const startStr = r?.행사시작일시 || r?.행사시작일 || null;
      const endStr = r?.행사종료일시 || null;
      const startDt = parseDateTime(startStr);
      const endDt = parseDateTime(endStr || startStr);
      if (!map.has(key)) {
        map.set(key, { minStart: null, minStartStr: '', maxEnd: null, maxEndStr: '' });
      }
      const e = map.get(key);
      if (startDt && (!e.minStart || startDt < e.minStart)) {
        e.minStart = startDt; e.minStartStr = startStr || '';
      }
      if (endDt && (!e.maxEnd || endDt > e.maxEnd)) {
        e.maxEnd = endDt; e.maxEndStr = endStr || startStr || '';
      }
    }
    return map;
  }, [rows]);

  // Map groupNo -> 대표 종류 (주문 상세 rows 기준, 첫 번째 유효 값 사용)
  const groupTypeByGroupNo = useMemo(() => {
    const m = new Map();
    const arr = Array.isArray(rows) ? rows : [];
    for (const r of arr) {
      const gno = r?.행사단체번호;
      if (gno === undefined || gno === null || String(gno) === '') continue;
      const key = String(gno);
      if (!m.has(key)) {
        const t = String(r?.종류 || '').trim();
        if (t) m.set(key, t);
      }
    }
    return m;
  }, [rows]);

  // apply UI filters
  const filteredRows = useMemo(() => {
    let arr = Array.isArray(rows) ? rows : [];
    // status filter
    if (statusFilter !== 'all') {
      arr = arr.filter((r) => {
        const st = String(r?.입금상태 || '');
        if (statusFilter === '완료') return st.includes('완료');
        if (statusFilter === '부분') return st.includes('부분');
        return !st.includes('완료') && !st.includes('부분'); // 기타
      });
    }
    // cancelled orders filter (only for orders tab)
    if (activeTab === 'orders' && !showCancelledOrders) {
      arr = arr.filter((r) => {
        const status = String(r?.예약상태2 || '').toLowerCase();
        return !status.includes('취소완료') && !status.includes('취소확정');
      });
    }
    // group filters
    const gno = groupNoFilter.trim();
    if (gno) {
      arr = arr.filter((r) => String(r?.행사단체번호 ?? '').includes(gno));
    }
    const gname = groupNameFilter.trim().toLowerCase();
    if (gname) {
      arr = arr.filter((r) => String(r?.행사단체명 ?? '').toLowerCase().includes(gname));
    }
    // keyword filter
    const q = keyword.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) => {
        const f1 = String(r?.상품명 || '').toLowerCase();
        const f2 = String(r?.예약고객명 || '').toLowerCase();
        return f1.includes(q) || f2.includes(q);
      });
    }
    return arr;
  }, [rows, statusFilter, keyword, groupNoFilter, groupNameFilter, activeTab, showCancelledOrders]);

  const orderHeaderCols = useMemo(() => (
    [
      { key: '예약일', label: '예약일', type: 'date' },
      { key: '출발일', label: '출발일', type: 'date' },
      { key: '판매처', label: '판매처', type: 'string' },
      { key: '상품명', label: '상품명', type: 'string' },
      { key: '예약고객명', label: '예약 고객명', type: 'string' },
      { key: '수량', label: '인원', type: 'number' },
      { key: '상품금액', label: '상품금액', type: 'number' },
      { key: '입금액', label: '입금액', type: 'number' },
      { key: '미수액', label: '미수액', type: 'number' },
      { key: '예약상태2', label: '예약상태', type: 'string' },
      { key: '입금상태', label: '입금상태', type: 'string' },
    ]
  ), []);

  useEffect(() => {
    const validKeys = new Set((orderHeaderCols || []).map((c) => c.key));

    setOrderHeaderFilterSelected((prev) => {
      const source = prev || {};
      const keys = Object.keys(source);
      if (keys.length === 0) return prev;
      let changed = false;
      const next = {};
      for (const k of keys) {
        if (validKeys.has(k)) next[k] = source[k];
        else changed = true;
      }
      return changed ? next : prev;
    });

    setOpenOrderHeaderFilterKey((prev) => {
      if (!prev) return prev;
      return validKeys.has(prev) ? prev : null;
    });

    setOrderSortKey((prev) => {
      if (!prev) return prev;
      return validKeys.has(prev) ? prev : null;
    });
  }, [orderHeaderCols]);

  const getOrderCellDisplay = (row, key) => {
    if (!row) return '';
    if (key === '예약일') {
      const raw = (row?.예약일 || '').toString();
      return raw.includes(' ') ? raw.split(' ')[0] : (raw.includes('T') ? raw.split('T')[0] : raw);
    }
    if (key === '출발일') {
      return formatDateOnly(row?.행사시작일시 || row?.행사시작일);
    }
    if (key === '미수액') {
      const amount = parseNumber(row?.상품금액);
      const dep = parseNumber(row?.입금액);
      const due = Math.max(0, amount - dep);
      return String(due);
    }
    const v = row?.[key];
    if (v == null) return '';
    return String(v);
  };

  const orderColumnValueOptions = useMemo(() => {
    const arr = Array.isArray(filteredRows) ? filteredRows : [];
    const map = {};
    orderHeaderCols.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getOrderCellDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [filteredRows, orderHeaderCols]);

  const toggleOrderHeaderFilter = (key, anchorEl = null) => {
    setOpenOrderHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        orderHeaderFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setOrderHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setOrderHeaderFilterAnchorRect(null);
        }
      } else {
        orderHeaderFilterAnchorElRef.current = null;
        setOrderHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const onOrderHeaderFilterSearchChange = (key, value) => {
    setOrderHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const isAllSelectedForOrderKey = (key) => {
    const opts = orderColumnValueOptions?.[key] || [];
    const selected = orderHeaderFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const toggleAllForOrderKey = (key) => {
    const opts = orderColumnValueOptions?.[key] || [];
    setOrderHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleOrderOptionForKey = (key, option) => {
    setOrderHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const ordersHeaderFilteredRows = useMemo(() => {
    if (activeTab !== 'orders') return filteredRows;
    const activeKeys = Object.keys(orderHeaderFilterSelected || {}).filter(
      (k) => Array.isArray(orderHeaderFilterSelected?.[k]) && orderHeaderFilterSelected[k].length > 0
    );
    if (activeKeys.length === 0) return filteredRows;
    const selectedMap = Object.fromEntries(activeKeys.map((k) => [k, new Set((orderHeaderFilterSelected[k] || []).map((v) => String(v)))]));
    return (Array.isArray(filteredRows) ? filteredRows : []).filter((row) =>
      activeKeys.every((k) => {
        const display = String(getOrderCellDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  }, [activeTab, filteredRows, orderHeaderFilterSelected]);

  const ordersSortedRows = useMemo(() => {
    if (activeTab !== 'orders') return ordersHeaderFilteredRows;
    if (!orderSortKey) return ordersHeaderFilteredRows;

    const meta = orderHeaderCols.find((c) => c.key === orderSortKey);
    const type = meta?.type || 'string';
    const arr = (Array.isArray(ordersHeaderFilteredRows) ? ordersHeaderFilteredRows : []).slice();
    arr.sort((a, b) => {
      const av = getOrderCellDisplay(a, orderSortKey);
      const bv = getOrderCellDisplay(b, orderSortKey);
      let cmp = 0;
      if (type === 'number') {
        const na = Number(String(av ?? '').replace(/,/g, ''));
        const nb = Number(String(bv ?? '').replace(/,/g, ''));
        const aNull = Number.isNaN(na);
        const bNull = Number.isNaN(nb);
        if (aNull && bNull) cmp = 0;
        else if (aNull) cmp = -1;
        else if (bNull) cmp = 1;
        else cmp = na - nb;
      } else if (type === 'date') {
        const ta = Date.parse(String(av ?? ''));
        const tb = Date.parse(String(bv ?? ''));
        const aNull = Number.isNaN(ta);
        const bNull = Number.isNaN(tb);
        if (aNull && bNull) cmp = 0;
        else if (aNull) cmp = -1;
        else if (bNull) cmp = 1;
        else cmp = ta - tb;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'ko');
      }
      return orderSortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [activeTab, ordersHeaderFilteredRows, orderSortKey, orderSortDir, orderHeaderCols]);

  const orderHeaderFilterPopover = useMemo(() => {
    if (activeTab !== 'orders') return null;
    if (!openOrderHeaderFilterKey) return null;
    if (!orderHeaderFilterAnchorRect) return null;

    const key = openOrderHeaderFilterKey;
    const widthPx = Math.max(240, Math.min(360, orderHeaderFilterAnchorRect.width || 0));
    const left = Math.max(8, orderHeaderFilterAnchorRect.left);
    const topBase = orderHeaderFilterAnchorRect.top + (orderHeaderFilterAnchorRect.height || 0) + 6;

    return (
      <div
        ref={orderHeaderFilterRootRef}
        style={{ position: 'fixed', top: topBase, left, width: widthPx }}
        className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-gray-100">
          <input
            type="text"
            placeholder="검색..."
            value={orderHeaderFilterSearch?.[key] ?? ''}
            onChange={(e) => onOrderHeaderFilterSearchChange(key, e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
          />
        </div>
        <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
          <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
            <input type="checkbox" checked={isAllSelectedForOrderKey(key)} onChange={() => toggleAllForOrderKey(key)} />
            <span>(모두 선택)</span>
          </label>
          <div className="my-1 border-t border-gray-100" />
          {(orderColumnValueOptions?.[key] || [])
            .filter((v) => {
              const q = String(orderHeaderFilterSearch?.[key] ?? '').trim().toLowerCase();
              if (!q) return true;
              return String(v).toLowerCase().includes(q);
            })
            .slice(0, 300)
            .map((v) => (
              <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={(orderHeaderFilterSelected?.[key] || []).includes(v)}
                  onChange={() => toggleOrderOptionForKey(key, v)}
                />
                <span className="truncate">{String(v)}</span>
              </label>
            ))}
        </div>
      </div>
    );
  }, [activeTab, openOrderHeaderFilterKey, orderHeaderFilterAnchorRect, orderHeaderFilterSearch, orderColumnValueOptions, orderHeaderFilterSelected]);

  // aggregates from filtered rows
  const totalSales = useMemo(() => filteredRows.reduce((sum, r) => sum + parseNumber(r?.상품금액), 0), [filteredRows]);
  const totalReceived = useMemo(() => filteredRows.reduce((sum, r) => sum + parseNumber(r?.입금액), 0), [filteredRows]);
  const totalReceivable = Math.max(0, totalSales - totalReceived);
  const totalQuantity = useMemo(() => filteredRows.reduce((sum, r) => sum + parseNumber(r?.수량), 0), [filteredRows]);
  const totalOutstandingReceivable = useMemo(() => {
    const arr = Array.isArray(outstandingRows) ? outstandingRows : [];
    const onlyOutstanding = arr.filter((r) => String(r?.입금상태 || '') === '미수');
    return onlyOutstanding.reduce((sum, r) => sum + parseNumber(r?.미수금액), 0);
  }, [outstandingRows]);

  // 전도금/예상수익 (row-level fields, if present)
  const totalPreDepositFromRows = useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + parseNumber(r?.행사전도금 ?? r?.전도금), 0);
  }, [filteredRows]);
  const totalExpectedProfitFromRows = useMemo(() => {
    return filteredRows.reduce((sum, r) => sum + parseNumber(r?.예상수익), 0);
  }, [filteredRows]);

  const completedPayments = useMemo(() => filteredRows.filter((r) => String(r?.입금상태 || '').includes('완료')).length, [filteredRows]);
  const partialPayments = useMemo(() => filteredRows.filter((r) => String(r?.입금상태 || '').includes('부분')).length, [filteredRows]);
  const refunds = useMemo(() => filteredRows.filter((r) => String(r?.예약상태2 || '').includes('취소') || String(r?.예약상태3 || '').includes('취소')).length, [filteredRows]);

  // (enrichment removed) product-wise summary removed

  // Filter settlement rows by group filters and keyword
  const filteredSettleRows = useMemo(() => {
    let arr = Array.isArray(settleRows) ? settleRows : [];
    const gno = groupNoFilter.trim();
    if (gno) arr = arr.filter((r) => String(r?.행사단체번호 ?? '').includes(gno));
    const gname = groupNameFilter.trim().toLowerCase();
    if (gname) arr = arr.filter((r) => String(r?.행사단체명 ?? '').toLowerCase().includes(gname));
    const q = keyword.trim().toLowerCase();
    if (q) {
      arr = arr.filter((r) => [r?.행사단체명, r?.수배담당자, r?.대리점].some((v) => String(v ?? '').toLowerCase().includes(q)));
    }
    // 예약상태 필터
    if (reservationStatusFilter !== 'all') {
      arr = arr.filter((r) => {
        const st = String(r?.예약상태명 || '');
        if (reservationStatusFilter === '확정') return st.includes('확정');
        if (reservationStatusFilter === '취소') return st.includes('취소');
        return !st.includes('확정') && !st.includes('취소'); // 기타
      });
    }
    return arr;
  }, [settleRows, groupNoFilter, groupNameFilter, keyword, reservationStatusFilter]);

  // Grouped dataset by group number for product-wise analysis (단체번호 기준 그룹핑)
  const groupedSettleRows = useMemo(() => {
    const rows = Array.isArray(filteredSettleRows) ? filteredSettleRows : [];
    // Stage 1: build per-group, per-sale snapshots to avoid double counting
    const groupSaleMap = new Map(); // groupNo -> Map(saleNoKey -> snapshotRow)
    const numericKeys = [
      '행사인원2', '원화환산액', '출금액', '미지급액', '수익합계', '최종수익', '판매현황NET', '판매현황입금액', '판매현황미수액',
    ];
    const getSaleNoKey = (r) => {
      const s = r?.판매번호 ?? r?.주문판매번호 ?? null;
      if (s == null || s === '') return `NO_SALE__${r?.주문번호 ?? Math.random()}`;
      return String(s);
    };
    const isCanceled = (row) => {
      const v = row?.취소여부 ?? row?.취소여부1 ?? row?.취소여부2 ?? '';
      const s = String(v).trim();
      return s === '1' || s.toLowerCase() === 'true';
    };
    for (const r of rows) {
      const rawGroupKey = r?.행사단체번호;
      const groupKey = (rawGroupKey != null && String(rawGroupKey).length > 0)
        ? String(rawGroupKey)
        : `NO_GROUP__${getSaleNoKey(r)}`;
      const saleKey = getSaleNoKey(r);
      if (!groupSaleMap.has(groupKey)) groupSaleMap.set(groupKey, new Map());
      const saleMap = groupSaleMap.get(groupKey);
      // overwrite snapshot per sale: assume row metrics represent that sale's totals
      const snap = { ...r };
      for (const k of numericKeys) snap[k] = parseNumber(r?.[k]);
      snap.__취소여부True = isCanceled(r);
      saleMap.set(saleKey, snap);
    }

    // Stage 2: aggregate per-group across unique sale snapshots
    const result = [];
    for (const [groupKey, saleMap] of groupSaleMap.entries()) {
      const sales = Array.from(saleMap.values());
      if (sales.length === 0) continue;
      const first = sales[0];
      const agg = { ...first };
      // 대표 키들
      agg.대표판매번호 = first?.판매번호 ?? first?.주문판매번호 ?? null;
      agg.대표주문번호 = first?.주문번호 ?? null;
      // 단체명/단체번호 보정
      agg.행사단체번호 = first?.행사단체번호 ?? (groupKey.startsWith('NO_GROUP__') ? '' : groupKey);
      if (!agg?.행사단체명) {
        const named = sales.find((x) => x?.행사단체명);
        if (named) agg.행사단체명 = named.행사단체명;
      }
      // 이벤트(행사번호) 기준 그대로 반영해야 하는 항목들은 첫 스냅샷 값을 사용
      agg.판매건수 = Number(first?.판매건수) || 0;
      agg.취소건수 = Number(first?.취소건수) || 0;
      agg.행사인원2 = Number(first?.행사인원2 ?? first?.행사인원) || 0;
      agg.최종수익 = Number(first?.최종수익) || 0;
      // 그 외 항목은 필요 시 합산 유지
      const sum = (key) => sales.reduce((acc, cur) => acc + (Number(cur?.[key]) || 0), 0);
      agg.원화환산액 = sum('원화환산액');
      agg.출금액 = sum('출금액');
      agg.미지급액 = sum('미지급액');
      agg.수익합계 = sum('수익합계');
      agg.판매현황NET = sum('판매현황NET');
      agg.판매현황입금액 = sum('판매현황입금액');
      agg.판매현황미수액 = sum('판매현황미수액');
      // 행사 시작/종료일시 보강: 주문(상세) 데이터에서 그룹번호 기준 대표 값 사용
      const realGroupNo = agg?.행사단체번호 ? String(agg.행사단체번호) : (groupKey.startsWith('NO_GROUP__') ? '' : groupKey);
      if (realGroupNo && groupDatesByGroupNo.has(realGroupNo)) {
        const d = groupDatesByGroupNo.get(realGroupNo);
        if (!agg.행사시작일시 && d.minStartStr) agg.행사시작일시 = d.minStartStr;
        if (!agg.행사종료일시 && d.maxEndStr) agg.행사종료일시 = d.maxEndStr;
      }
      // 종류 보강: 주문 상세 rows에서 그룹번호 기준 대표 종류 사용
      if (!agg.종류 && realGroupNo && groupTypeByGroupNo.has(realGroupNo)) {
        agg.종류 = groupTypeByGroupNo.get(realGroupNo);
      }
      // 추가 보강: 첫 스냅샷에 종류가 있으면 사용
      if (!agg.종류 && first?.종류) {
        agg.종류 = first.종류;
      }
      result.push(agg);
    }
    return result;
  }, [filteredSettleRows, groupDatesByGroupNo, groupTypeByGroupNo]);

  const groupHeaderCols = useMemo(() => (
    [
      { key: '행사단체명', label: '행사단체명', type: 'string' },
      { key: '행사단체번호', label: '단체번호', type: 'string' },
      { key: '종류', label: '종류', type: 'string' },
      { key: '행사시작일시', label: '행사 시작일', type: 'date' },
      { key: '행사종료일시', label: '행사 종료일', type: 'date' },
      { key: '판매건수', label: '판매건수', type: 'number' },
      { key: '취소건수', label: '취소건수', type: 'number' },
      { key: '행사인원2', label: '행사인원', type: 'number' },
      { key: '판매현황NET', label: '판매가', type: 'number' },
      { key: '원화환산액', label: '전도금', type: 'number' },
      { key: '최종수익', label: '최종수익', type: 'number' },
      { key: '예약상태명', label: '예약상태', type: 'string' },
      { key: '마감상태명', label: '정산 마감', type: 'string' },
    ]
  ), []);

  const getGroupCellDisplay = (row, key) => {
    if (!row) return '';
    if (key === '행사시작일시') return formatDateOnly(row?.행사시작일시);
    if (key === '행사종료일시') return formatDateOnly(row?.행사종료일시);
    const v = row?.[key];
    if (v == null) return '';
    return String(v);
  };

  const groupColumnValueOptions = useMemo(() => {
    const arr = Array.isArray(groupedSettleRows) ? groupedSettleRows : [];
    const map = {};
    groupHeaderCols.forEach((c) => {
      const set = new Set();
      arr.forEach((row) => {
        const v = String(getGroupCellDisplay(row, c.key) ?? '').trim();
        if (v !== '') set.add(v);
      });
      map[c.key] = Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
    });
    return map;
  }, [groupedSettleRows, groupHeaderCols]);

  const toggleGroupHeaderFilter = (key, anchorEl = null) => {
    setOpenGroupHeaderFilterKey((prev) => {
      const next = prev === key ? null : key;
      if (next) {
        groupHeaderFilterAnchorElRef.current = anchorEl;
        if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
          const r = anchorEl.getBoundingClientRect();
          setGroupHeaderFilterAnchorRect({ left: r.left, top: r.top, height: r.height, width: r.width });
        } else {
          setGroupHeaderFilterAnchorRect(null);
        }
      } else {
        groupHeaderFilterAnchorElRef.current = null;
        setGroupHeaderFilterAnchorRect(null);
      }
      return next;
    });
  };

  const onGroupHeaderFilterSearchChange = (key, value) => {
    setGroupHeaderFilterSearch((prev) => ({ ...prev, [key]: value }));
  };

  const isAllSelectedForGroupKey = (key) => {
    const opts = groupColumnValueOptions?.[key] || [];
    const selected = groupHeaderFilterSelected?.[key] || [];
    return opts.length > 0 && selected.length === opts.length;
  };

  const toggleAllForGroupKey = (key) => {
    const opts = groupColumnValueOptions?.[key] || [];
    setGroupHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const next = current.length === opts.length ? [] : opts;
      return { ...prev, [key]: next };
    });
  };

  const toggleGroupOptionForKey = (key, option) => {
    setGroupHeaderFilterSelected((prev) => {
      const current = prev?.[key] || [];
      const exists = current.includes(option);
      const next = exists ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [key]: next };
    });
  };

  const groupHeaderFilteredRows = useMemo(() => {
    if (activeTab !== 'group') return groupedSettleRows;
    const activeKeys = Object.keys(groupHeaderFilterSelected || {}).filter(
      (k) => Array.isArray(groupHeaderFilterSelected?.[k]) && groupHeaderFilterSelected[k].length > 0
    );
    if (activeKeys.length === 0) return groupedSettleRows;
    const selectedMap = Object.fromEntries(activeKeys.map((k) => [k, new Set((groupHeaderFilterSelected[k] || []).map((v) => String(v)))]));
    return (Array.isArray(groupedSettleRows) ? groupedSettleRows : []).filter((row) =>
      activeKeys.every((k) => {
        const display = String(getGroupCellDisplay(row, k) ?? '').trim();
        return selectedMap[k].has(display);
      })
    );
  }, [activeTab, groupedSettleRows, groupHeaderFilterSelected]);

  const groupSortedRows = useMemo(() => {
    if (activeTab !== 'group') return groupHeaderFilteredRows;
    if (!groupSortKey) return groupHeaderFilteredRows;

    const meta = groupHeaderCols.find((c) => c.key === groupSortKey);
    const type = meta?.type || 'string';
    const arr = (Array.isArray(groupHeaderFilteredRows) ? groupHeaderFilteredRows : []).slice();
    arr.sort((a, b) => {
      const av = getGroupCellDisplay(a, groupSortKey);
      const bv = getGroupCellDisplay(b, groupSortKey);
      let cmp = 0;
      if (type === 'number') {
        const na = Number(String(av ?? '').replace(/,/g, ''));
        const nb = Number(String(bv ?? '').replace(/,/g, ''));
        const aNull = Number.isNaN(na);
        const bNull = Number.isNaN(nb);
        if (aNull && bNull) cmp = 0;
        else if (aNull) cmp = -1;
        else if (bNull) cmp = 1;
        else cmp = na - nb;
      } else if (type === 'date') {
        const ta = Date.parse(String(av ?? ''));
        const tb = Date.parse(String(bv ?? ''));
        const aNull = Number.isNaN(ta);
        const bNull = Number.isNaN(tb);
        if (aNull && bNull) cmp = 0;
        else if (aNull) cmp = -1;
        else if (bNull) cmp = 1;
        else cmp = ta - tb;
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'ko');
      }
      return groupSortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [activeTab, groupHeaderFilteredRows, groupSortKey, groupSortDir, groupHeaderCols]);

  const groupHeaderFilterPopover = useMemo(() => {
    if (activeTab !== 'group') return null;
    if (!openGroupHeaderFilterKey) return null;
    if (!groupHeaderFilterAnchorRect) return null;

    const key = openGroupHeaderFilterKey;
    const widthPx = Math.max(240, Math.min(360, groupHeaderFilterAnchorRect.width || 0));
    const left = Math.max(8, groupHeaderFilterAnchorRect.left);
    const topBase = groupHeaderFilterAnchorRect.top + (groupHeaderFilterAnchorRect.height || 0) + 6;

    return (
      <div
        ref={groupHeaderFilterRootRef}
        style={{ position: 'fixed', top: topBase, left, width: widthPx }}
        className="bg-white border border-gray-200 rounded-md shadow-lg z-50"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="p-2 border-b border-gray-100">
          <input
            type="text"
            placeholder="검색..."
            value={groupHeaderFilterSearch?.[key] ?? ''}
            onChange={(e) => onGroupHeaderFilterSearchChange(key, e.target.value)}
            className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
          />
        </div>
        <div className="max-h-56 overflow-auto p-2 text-xs text-gray-700">
          <label className="flex items-center gap-2 py-1 cursor-pointer select-none">
            <input type="checkbox" checked={isAllSelectedForGroupKey(key)} onChange={() => toggleAllForGroupKey(key)} />
            <span>(모두 선택)</span>
          </label>
          <div className="my-1 border-t border-gray-100" />
          {(groupColumnValueOptions?.[key] || [])
            .filter((v) => {
              const q = String(groupHeaderFilterSearch?.[key] ?? '').trim().toLowerCase();
              if (!q) return true;
              return String(v).toLowerCase().includes(q);
            })
            .slice(0, 300)
            .map((v) => (
              <label key={String(v)} className="flex items-center gap-2 py-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={(groupHeaderFilterSelected?.[key] || []).includes(v)}
                  onChange={() => toggleGroupOptionForKey(key, v)}
                />
                <span className="truncate">{String(v)}</span>
              </label>
            ))}
        </div>
      </div>
    );
  }, [activeTab, openGroupHeaderFilterKey, groupHeaderFilterAnchorRect, groupHeaderFilterSearch, groupColumnValueOptions, groupHeaderFilterSelected]);

  // 전도금 합계: 행사단체 결산(그룹핑) 기준으로 원화환산액 합계 사용
  const totalPreDepositFromGroups = useMemo(() => {
    const arr = Array.isArray(groupedSettleRows) ? groupedSettleRows : [];
    return arr.reduce((sum, r) => sum + parseNumber(r?.원화환산액), 0);
  }, [groupedSettleRows]);

  // Product-wise grouped summary from filtered order rows
  const groupedProductRows = useMemo(() => {
    const map = new Map(); // key (종류|상품명) -> agg
    for (const r of filteredRows) {
      const name = String(r?.상품명 || '').trim() || '-';
      const type = String(r?.종류 || '').trim() || '-';
      const key = `${type}|${name}`;
      const amount = parseNumber(r?.상품금액);
      const dep = parseNumber(r?.입금액);
      const status = String(r?.입금상태 || '');
      const reservationStatus2 = String(r?.예약상태2 || '');
      let agg = map.get(key);
      if (!agg) {
        const productNo = r?.상품번호 ?? r?.productNo ?? r?.ProductNo ?? r?.판매상품번호 ?? r?.상품ID;
        const productCode = r?.상품코드 ?? r?.ProductCode ?? r?.productCode ?? r?.판매상품코드;
        agg = {
          종류: type,
          상품명: name,
          상품번호: productNo ?? null,
          상품코드: productCode ?? '',
          건수: 0,
          총판매가: 0,
          총입금액: 0,
          총미수액: 0,
          완료건: 0,
          부분건: 0,
          취소완료건: 0,
        };
        map.set(key, agg);
      }
      agg.건수 += 1;
      agg.총판매가 += amount;
      agg.총입금액 += dep;
      agg.총미수액 += Math.max(0, amount - dep);
      if (status.includes('완료')) agg.완료건 += 1;
      else if (status.includes('부분')) agg.부분건 += 1;
      if (reservationStatus2.includes('취소완료')) agg.취소완료건 += 1;
    }
    const arr = Array.from(map.values());
    // sort by total sales desc
    arr.sort((a, b) => b.총판매가 - a.총판매가);
    return arr;
  }, [filteredRows]);

  const openGroupDrawer = (row) => {
    const s = row?.대표판매번호 ?? row?.판매번호 ?? null;
    if (!s) return;
    setDrawerSaleNo(s);
    setDrawerTitle(row?.행사단체명 || '단체정보');
    setDrawerOpen(true);
  };

  const openReservationDetailFromGroup = (row) => {
    const orderNo = row?.대표주문번호 ?? row?.주문번호 ?? null;
    if (!orderNo) return;
    const saleNo = row?.대표판매번호 ?? row?.판매번호 ?? null;
    window.open(
      `/reservation-detail/${orderNo}?saleNo=${saleNo}&productName=${encodeURIComponent(row?.행사단체명 || '')}`,
      '_blank'
    );
  };

  const openReservationDetailFromOrder = (row) => {
    const orderNo = row?.주문번호 ?? row?.orderNo;
    const saleNo = row?.판매번호 ?? row?.주문판매번호;
    const productName = row?.상품명;
    if (!orderNo) return;
    window.open(
      `/reservation-detail/${orderNo}?saleNo=${saleNo}&productName=${encodeURIComponent(productName || '')}`,
      '_blank'
    );
  };

  const groupTableColumns = useMemo(
    () => [
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">행사단체명</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('행사단체명'); }}>
              {groupSortKey === '행사단체명' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.행사단체명) && groupHeaderFilterSelected.행사단체명.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('행사단체명', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        key: '행사단체명',
        dataIndex: '행사단체명',
        width: 260,
        align: 'left',
        render: (_, row) => (
          <button
            type="button"
            className="text-sm font-mono text-black-600 hover:text-blue-800 hover:underline transition-colors flex items-start gap-1"
            onClick={(e) => {
              e.stopPropagation();
              openGroupDrawer(row);
            }}
            title="단체정보 보기"
          >
           
            <span className="line-clamp-2 text-left">{row?.행사단체명 || '-'}</span>
          </button>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">단체번호</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('행사단체번호'); }}>
              {groupSortKey === '행사단체번호' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.행사단체번호) && groupHeaderFilterSelected.행사단체번호.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('행사단체번호', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '행사단체번호',
        key: '행사단체번호',
        width: 120,
        align: 'left',
        render: (v) => <span className="font-mono text-sm text-gray-600">{v ?? ''}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">종류</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('종류'); }}>
              {groupSortKey === '종류' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.종류) && groupHeaderFilterSelected.종류.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('종류', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '종류',
        key: '종류',
        width: 100,
        align: 'left',
        render: (v) => (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{v || '-'}</span>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">행사 시작일</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('행사시작일시'); }}>
              {groupSortKey === '행사시작일시' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.행사시작일시) && groupHeaderFilterSelected.행사시작일시.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('행사시작일시', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '행사시작일시',
        key: '행사시작일시',
        width: 130,
        align: 'left',
        render: (_, row) => <span className="text-sm text-gray-600">{formatDateOnly(row?.행사시작일시)}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">행사 종료일</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('행사종료일시'); }}>
              {groupSortKey === '행사종료일시' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.행사종료일시) && groupHeaderFilterSelected.행사종료일시.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('행사종료일시', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '행사종료일시',
        key: '행사종료일시',
        width: 130,
        align: 'left',
        render: (_, row) => <span className="text-sm text-gray-600">{formatDateOnly(row?.행사종료일시)}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">판매건수</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('판매건수'); }}>
              {groupSortKey === '판매건수' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.판매건수) && groupHeaderFilterSelected.판매건수.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('판매건수', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '판매건수',
        key: '판매건수',
        width: 100,
        align: 'left',
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">취소건수</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('취소건수'); }}>
              {groupSortKey === '취소건수' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.취소건수) && groupHeaderFilterSelected.취소건수.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('취소건수', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '취소건수',
        key: '취소건수',
        width: 100,
        align: 'left',
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">행사인원</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('행사인원2'); }}>
              {groupSortKey === '행사인원2' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.행사인원2) && groupHeaderFilterSelected.행사인원2.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('행사인원2', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '행사인원2',
        key: '행사인원2',
        width: 100,
        align: 'left',
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">판매가</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('판매현황NET'); }}>
              {groupSortKey === '판매현황NET' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.판매현황NET) && groupHeaderFilterSelected.판매현황NET.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('판매현황NET', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '판매현황NET',
        key: '판매현황NET',
        width: 140,
        align: 'left',
        render: (v) => <span className="font-mono tabular-nums">{formatCurrency(parseNumber(v))}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">전도금</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('원화환산액'); }}>
              {groupSortKey === '원화환산액' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.원화환산액) && groupHeaderFilterSelected.원화환산액.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('원화환산액', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '원화환산액',
        key: '원화환산액',
        width: 140,
        align: 'left',
        render: (v) => <span className="font-mono tabular-nums">{formatCurrency(parseNumber(v))}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">최종수익</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('최종수익'); }}>
              {groupSortKey === '최종수익' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.최종수익) && groupHeaderFilterSelected.최종수익.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('최종수익', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '최종수익',
        key: '최종수익',
        width: 140,
        align: 'left',
        render: (v) => {
          const n = parseNumber(v);
          const cls = n > 0
            ? 'bg-green-50 text-green-700'
            : n < 0
              ? 'bg-red-50 text-red-700'
              : 'bg-gray-50 text-gray-700';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls} tabular-nums`}>{formatCurrency(n)}</span>
          );
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">예약상태</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('예약상태명'); }}>
              {groupSortKey === '예약상태명' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.예약상태명) && groupHeaderFilterSelected.예약상태명.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('예약상태명', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '예약상태명',
        key: '예약상태명',
        width: 120,
        align: 'left',
        render: (v) => {
          const s = String(v || '');
          const cls = s.includes('확정')
            ? 'bg-green-50 text-green-700'
            : s.includes('취소')
              ? 'bg-red-50 text-red-700'
              : 'bg-amber-50 text-amber-700';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s || '-'}</span>
          );
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">정산 마감</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleGroupSort('마감상태명'); }}>
              {groupSortKey === '마감상태명' ? (groupSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(groupHeaderFilterSelected?.마감상태명) && groupHeaderFilterSelected.마감상태명.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleGroupHeaderFilter('마감상태명', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '마감상태명',
        key: '마감상태명',
        width: 120,
        align: 'left',
        render: (v) => {
          const s = String(v || '-');
          const cls = s === '마감' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-700';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s}</span>
          );
        },
      },
    ],
    [formatCurrency, formatDateOnly, parseNumber, groupSortKey, groupSortDir, groupHeaderFilterSelected, openGroupHeaderFilterKey]
  );

  const groupTotalTableWidth = useMemo(
    () => groupTableColumns.reduce((sum, c) => sum + (c.width || 160), 0),
    [groupTableColumns]
  );

  const orderTableColumns = useMemo(
    () => [
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">예약일</span>
            <button
              type="button"
              className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500"
              title="정렬"
              onClick={(e) => { e.stopPropagation(); toggleOrderSort('예약일'); }}
            >
              {orderSortKey === '예약일' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button
              type="button"
              className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.예약일) && orderHeaderFilterSelected.예약일.length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
              title="필터"
              onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('예약일', e.currentTarget); }}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        key: '예약일표시',
        width: 120,
        align: 'left',
        render: (_, row) => {
          const raw = (row?.예약일 || '').toString();
          const display = raw.includes(' ') ? raw.split(' ')[0] : (raw.includes('T') ? raw.split('T')[0] : raw);
          return <span className="font-mono text-sm text-gray-600">{display}</span>;
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">출발일</span>
            <button
              type="button"
              className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500"
              title="정렬"
              onClick={(e) => { e.stopPropagation(); toggleOrderSort('출발일'); }}
            >
              {orderSortKey === '출발일' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button
              type="button"
              className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.출발일) && orderHeaderFilterSelected.출발일.length > 0 ? 'text-primary-600' : 'text-gray-500'}`}
              title="필터"
              onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('출발일', e.currentTarget); }}
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        key: '출발일표시',
        width: 120,
        align: 'left',
        render: (_, row) => (
          <span className="font-mono text-sm text-gray-600">{formatDateOnly(row?.행사시작일시 || row?.행사시작일)}</span>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">판매처</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('판매처'); }}>
              {orderSortKey === '판매처' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.판매처) && orderHeaderFilterSelected.판매처.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('판매처', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '판매처',
        key: '판매처',
        width: 140,
        align: 'left',
        render: (v) => (
          <span className="text-sm text-gray-700 whitespace-nowrap">{v || '-'}</span>
        ),
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">상품명</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('상품명'); }}>
              {orderSortKey === '상품명' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.상품명) && orderHeaderFilterSelected.상품명.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('상품명', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '상품명',
        key: '상품명',
        width: 320,
        align: 'left',
        ellipsis: true,
        render: (v) => <span className="text-sm text-gray-700 whitespace-nowrap">{v}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">예약 고객명</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('예약고객명'); }}>
              {orderSortKey === '예약고객명' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.예약고객명) && orderHeaderFilterSelected.예약고객명.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('예약고객명', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '예약고객명',
        key: '예약고객명',
        width: 140,
        align: 'left',
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">인원</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('수량'); }}>
              {orderSortKey === '수량' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.수량) && orderHeaderFilterSelected.수량.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('수량', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '수량',
        key: '인원',
        width: 80,
        align: 'left',
        render: (v) => {
          const n = parseNumber(v);
          return <span className="font-mono tabular-nums">{n ? n.toLocaleString('ko-KR') : ''}</span>;
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">상품금액</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('상품금액'); }}>
              {orderSortKey === '상품금액' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.상품금액) && orderHeaderFilterSelected.상품금액.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('상품금액', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '상품금액',
        key: '상품금액',
        width: 140,
        align: 'left',
        render: (v) => <span className="font-mono tabular-nums">{formatCurrency(parseNumber(v))}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">입금액</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('입금액'); }}>
              {orderSortKey === '입금액' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.입금액) && orderHeaderFilterSelected.입금액.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('입금액', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '입금액',
        key: '입금액',
        width: 140,
        align: 'left',
        render: (v) => <span className="font-mono tabular-nums">{formatCurrency(parseNumber(v))}</span>,
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">미수액</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('미수액'); }}>
              {orderSortKey === '미수액' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.미수액) && orderHeaderFilterSelected.미수액.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('미수액', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        key: '미수액',
        width: 140,
        align: 'left',
        render: (_, row) => {
          const amount = parseNumber(row?.상품금액);
          const dep = parseNumber(row?.입금액);
          const due = Math.max(0, amount - dep);
          const cls = due > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls} tabular-nums`}>{formatCurrency(due)}</span>
          );
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">예약상태</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('예약상태2'); }}>
              {orderSortKey === '예약상태2' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.예약상태2) && orderHeaderFilterSelected.예약상태2.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('예약상태2', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '예약상태2',
        key: '예약상태2',
        width: 120,
        align: 'left',
        render: (v) => {
          const s = String(v || '-');
          const cls = s.includes('확정')
            ? 'bg-green-50 text-green-700'
            : s.includes('취소')
              ? 'bg-red-50 text-red-700'
              : 'bg-amber-50 text-amber-700';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{s}</span>
          );
        },
      },
      {
        title: (
          <div className="flex items-center justify-start gap-2">
            <span className="whitespace-nowrap">입금상태</span>
            <button type="button" className="text-[10px] px-1 py-0.5 rounded hover:bg-gray-50 text-gray-500" title="정렬" onClick={(e) => { e.stopPropagation(); toggleOrderSort('입금상태'); }}>
              {orderSortKey === '입금상태' ? (orderSortDir === 'asc' ? '▲' : '▼') : '↕'}
            </button>
            <button type="button" className={`inline-flex items-center justify-center h-7 w-7 rounded hover:bg-gray-100 ${Array.isArray(orderHeaderFilterSelected?.입금상태) && orderHeaderFilterSelected.입금상태.length > 0 ? 'text-primary-600' : 'text-gray-500'}`} title="필터" onClick={(e) => { e.stopPropagation(); toggleOrderHeaderFilter('입금상태', e.currentTarget); }}>
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
            </button>
          </div>
        ),
        dataIndex: '입금상태',
        key: '입금상태',
        width: 120,
        align: 'left',
        render: (v) => {
          const s = String(v || '-');
          const badgeClass = s.includes('완납')
            ? 'bg-green-100 text-green-800'
            : s.includes('부분')
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-red-100 text-red-800';
          return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badgeClass}`}>{s}</span>
          );
        },
      },
    ],
    [formatCurrency, formatDateOnly, parseNumber, orderSortKey, orderSortDir, orderHeaderFilterSelected]
  );

  const ordersTotalTableWidth = useMemo(
    () => orderTableColumns.reduce((sum, c) => sum + (c.width || 160), 0),
    [orderTableColumns]
  );

  const handleExportCsv = () => {
    try {
      const period = activeSearchState?.period;
      const selectedMonth = activeSearchState?.selectedMonth;
      const selectedQuarter = activeSearchState?.selectedQuarter;
      const selectedYear = activeSearchState?.selectedYear;
      const headers = ['주문번호','예약일','판매처','상품명','예약고객명','상품금액','입금액','미수액','입금상태'];
      const lines = [headers.join(',')];
      for (const row of filteredRows) {
        const amount = parseNumber(row?.상품금액);
        const dep = parseNumber(row?.입금액);
        const due = Math.max(0, amount - dep);
        const 예약일 = (row?.예약일 || '').toString();
        const 예약일표시 = 예약일.includes(' ') ? 예약일.split(' ')[0] : (예약일.includes('T') ? 예약일.split('T')[0] : 예약일);
        const values = [
          row?.주문번호 ?? '',
          예약일표시,
          row?.판매처 ?? '',
          row?.상품명 ?? '',
          row?.예약고객명 ?? '',
          amount,
          dep,
          due,
          String(row?.입금상태 || ''),
        ];
        const escaped = values.map((v) => {
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        });
        lines.push(escaped.join(','));
      }
      const csv = '\ufeff' + lines.join('\n'); // BOM for Excel
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const label = period === 'monthly' ? selectedMonth : (period === 'quarterly' ? selectedQuarter : selectedYear);
      a.href = url;
      a.download = `settlement_${label}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
      alert('엑셀 저장에 실패했습니다.');
    }
  };

  return (
    <div className="space-y-6">
      {travelerPopover.open && travelerPopover.anchorRect ? (() => {
        const rect = travelerPopover.anchorRect;
        const popoverWidth = 1080;
        const popoverMaxHeight = 360;
        const margin = 8;
        const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
        const preferredLeft = rect.right + 10;
        const left = Math.max(margin, Math.min(preferredLeft + popoverWidth > viewportW ? rect.left - 10 - popoverWidth : preferredLeft, viewportW - margin - popoverWidth));
        const top = Math.max(margin, Math.min(rect.top, viewportH - margin - popoverMaxHeight));
        return createPortal(
          <div
            ref={travelerPopoverRootRef}
            style={{ position: 'fixed', top, left, width: popoverWidth, maxHeight: popoverMaxHeight, zIndex: 1200 }}
            className="bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden"
            onMouseEnter={() => {
              if (travelerCloseTimerRef.current) {
                clearTimeout(travelerCloseTimerRef.current);
                travelerCloseTimerRef.current = null;
              }
            }}
            onMouseLeave={() => closeTravelerPopover()}
          >
            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">여행객 명단</div>
              <div className="text-xs text-gray-500">{Array.isArray(travelerPopover.list) ? travelerPopover.list.length : 0}명</div>
            </div>
            <div className="p-3 overflow-auto" style={{ maxHeight: popoverMaxHeight - 44 }}>
              {travelerPopover.loading && (
                <div className="text-sm text-gray-600">불러오는 중입니다...</div>
              )}
              {!travelerPopover.loading && travelerPopover.error && (
                <div className="text-sm text-red-600">{travelerPopover.error}</div>
              )}
              {!travelerPopover.loading && !travelerPopover.error && (!travelerPopover.list || travelerPopover.list.length === 0) && (
                <div className="text-sm text-gray-500">표시할 여행객이 없습니다.</div>
              )}
              {!travelerPopover.loading && !travelerPopover.error && Array.isArray(travelerPopover.list) && travelerPopover.list.length > 0 && (
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-gray-500">
                      <th className="text-left font-medium pb-2 pr-2">한글이름</th>
                      <th className="text-center font-medium pb-2 pr-2">성별</th>
                      <th className="text-left font-medium pb-2 pr-2">영문성</th>
                      <th className="text-left font-medium pb-2 pr-2">영문이름</th>
                      <th className="text-center font-medium pb-2 pr-2">생년월일</th>
                      <th className="text-left font-medium pb-2 pr-2">여권번호</th>
                      <th className="text-center font-medium pb-2 pr-2">여권만료일</th>
                      <th className="text-left font-medium pb-2">여행객비고</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800">
                    {travelerPopover.list.map((t, idx) => (
                      <tr key={String(t?.여행객번호 ?? idx)} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{t?.한글이름 || '-'}</td>
                        <td className="py-1.5 pr-2 text-center whitespace-nowrap">{t?.성별 || '-'}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{t?.영문성 || '-'}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{t?.영문이름 || '-'}</td>
                        <td className="py-1.5 pr-2 text-center whitespace-nowrap">{formatTravelerDate(t?.생년월일)}</td>
                        <td className="py-1.5 pr-2 whitespace-nowrap">{t?.여권번호 || '-'}</td>
                        <td className="py-1.5 pr-2 text-center whitespace-nowrap">{formatTravelerDate(t?.여권만료일)}</td>
                        <td className="py-1.5 whitespace-pre-wrap break-words">{t?.여행객비고 || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>,
          document.body
        );
      })() : null}
      <div className="sticky top-0 z-20 bg-white">
        <div className="px-0 py-2">
          <div className="inline-flex items-center p-1 rounded-xl" role="tablist" aria-label="정산 목록 전환">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'reservationSearch'}
              className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'reservationSearch'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('reservationSearch')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                예약조회
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'orders'}
              className={`ml-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'orders' 
                  ? 'bg-white text-blue-600 shadow-md' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('orders')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                주문조회
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'group'}
              className={`ml-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'group' 
                  ? 'bg-white text-blue-600 shadow-md' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('group')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                단체조회
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'product'}
              className={`ml-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'product' 
                  ? 'bg-white text-blue-600 shadow-md' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('product')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                상품별 조회
              </span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'stats'}
              className={`ml-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'stats'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              onClick={() => setActiveTab('stats')}
            >
              <span className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m4 6V7m4 10v-4m1 8H6a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2z" />
                </svg>
                실적 통계
              </span>
            </button>
          </div>
        </div>
      </div>
      {/* Header Toolbar - Modern Design */}
      {activeTab !== 'reservationSearch' && (
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            {(activeTab === 'orders' || activeTab === 'product' || activeTab === 'stats') && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">조회구분</span>
                <select
                  id="orderSearchBasis"
                  value={activeSearchState?.orderSearchBasis}
                  onChange={(e) => handleSearchBasisChange(e.target.value)}
                  className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                >
                  <option value="1">예약일</option>
                  <option value="2">출발일</option>
                </select>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-600">조회기간</span>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={activeSearchState?.manualStartDate}
                  onChange={(e) => updateActiveSearchState({ manualStartDate: e.target.value })}
                  className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                />
                <span className="text-xs text-gray-500">~</span>
                <input
                  type="date"
                  value={activeSearchState?.manualEndDate}
                  onChange={(e) => updateActiveSearchState({ manualEndDate: e.target.value })}
                  className="px-2 h-9 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                />
                {(activeSearchState?.manualStartDate || activeSearchState?.manualEndDate) && (
                  <button
                    type="button"
                    onClick={() => { updateActiveSearchState({ manualStartDate: '', manualEndDate: '' }); }}
                    className="px-2 py-1 text-[11px] rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    title="기간 초기화"
                  >지우기</button>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-2">
              <span className="text-[11px] text-gray-500">빠른 선택</span>
              <button
                type="button"
                onClick={setToToday}
                className={`px-2.5 py-1 rounded border text-xs ${
                  getActiveQuickSelection() === 'today'
                    ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="오늘"
              >오늘</button>
              <button
                type="button"
                onClick={setToOneWeek}
                className={`px-2.5 py-1 rounded border text-xs ${
                  getActiveQuickSelection() === 'week'
                    ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="일주일"
              >일주일</button>
              <button
                type="button"
                onClick={setToOneMonth}
                className={`px-2.5 py-1 rounded border text-xs ${
                  getActiveQuickSelection() === 'month'
                    ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="한달"
              >한달</button>
              <button
                type="button"
                onClick={setToThreeMonths}
                className={`px-2.5 py-1 rounded border text-xs ${
                  getActiveQuickSelection() === 'threeMonths'
                    ? 'border-primary-500 bg-primary-600 text-white hover:bg-primary-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="3개월"
              >3개월</button>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="show-cancelled-orders"
                type="checkbox"
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                checked={showCancelledOrders}
                onChange={(e) => setShowCancelledOrders(e.target.checked)}
              />
              <label htmlFor="show-cancelled-orders" className="text-xs text-gray-600 select-none">
                취소주문 보기
              </label>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center px-3 py-1.5 text-xs rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 01.78 1.625L12 12.25V16a1 1 0 01-1.447.894l-2-1A1 1 0 018 15v-2.75L2.22 5.625A1 1 0 013 5z" clipRule="evenodd"/></svg>
              {filtersOpen ? '필터 접기' : '필터'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-3.5 py-1.5 text-xs rounded bg-primary-600 text-white hover:bg-primary-700 shadow-sm"
              onClick={handleExportCsv}
            >
              <svg className="h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
              엑셀 다운로드
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Collapsible Filters - Modern Design */}
      {filtersOpen && (
        <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-lg border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              상세 필터
            </h3>
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm transition-colors"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              초기화
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">입금 상태</label>
              <select
                className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="완료">결제 완료</option>
                <option value="부분">부분 결제</option>
                <option value="기타">미입금/기타</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">예약 상태</label>
              <select
                className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                value={reservationStatusFilter}
                onChange={(e) => setReservationStatusFilter(e.target.value)}
              >
                <option value="all">전체</option>
                <option value="확정">확정</option>
                <option value="취소">취소</option>
                <option value="기타">기타</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">행사단체번호</label>
              <input
                type="text"
                value={groupNoFilter}
                onChange={(e) => setGroupNoFilter(e.target.value)}
                placeholder="예: 12345"
                className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">행사단체명</label>
              <input
                type="text"
                value={groupNameFilter}
                onChange={(e) => setGroupNameFilter(e.target.value)}
                placeholder="예: 단체명"
                className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              />
            </div>
            <div className="lg:col-span-2 space-y-2">
              <label className="block text-sm font-medium text-gray-700">검색 (상품명/예약 고객명)</label>
              <div className="relative">
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="예: 제주, 홍길동"
                  className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                />
                <svg className="absolute right-3 top-3 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

         <Modal
        open={preDepositModalOpen}
        title={<div className="text-base font-semibold">전도금 상세 내역</div>}
        onCancel={() => setPreDepositModalOpen(false)}
        footer={null}
        width={900}
      >
        <div className="mb-3 text-xs text-gray-500">단체 기준으로 집계된 전도금 내역입니다. 행을 클릭하면 단체 상세로 이동합니다.</div>
        <Table
          size="small"
          rowKey={(r) => `${r.행사단체번호 || 'NO'}_${r.대표판매번호 || r.대표주문번호 || Math.random()}`}
          rowClassName={() => 'cursor-pointer hover:bg-gray-50 transition-colors'}
          dataSource={useMemo(() => {
            const arr = Array.isArray(groupedSettleRows) ? groupedSettleRows : [];
            const enriched = arr.map((r) => ({
              ...r,
              __전도금: parseNumber(r?.원화환산액),
              __출금액: parseNumber(r?.출금액),
              __미지급액: parseNumber(r?.미지급액),
            }))
            .filter((r) => (r.__전도금 || 0) > 0);
            enriched.sort((a, b) => b.__전도금 - a.__전도금);
            return enriched;
          }, [groupedSettleRows])}
          columns={[
            { title: '행사단체번호', dataIndex: '행사단체번호', width: 120 },
            { title: '행사단체명', dataIndex: '행사단체명', ellipsis: true },
            {
              title: '기간',
              key: '기간',
              width: 200,
              render: (_, row) => {
                const s = formatDateOnly(row?.행사시작일시);
                const e = formatDateOnly(row?.행사종료일시);
                return <span className="text-gray-600">{s || '-'} ~ {e || '-'}</span>;
              },
            },
            {
              title: '전도금',
              dataIndex: '__전도금',
              align: 'right',
              width: 120,
              render: (v) => <span className="font-medium">{formatCurrency(v)}</span>,
            },
            {
              title: '출금액',
              dataIndex: '__출금액',
              align: 'right',
              width: 120,
              render: (v) => <span>{formatCurrency(v)}</span>,
            },
          ]}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          onRow={(row) => ({
            onClick: () => {
              const saleNo = row?.대표판매번호 || row?.주문판매번호 || row?.대표주문번호 || null;
              if (saleNo) {
                setDrawerSaleNo(saleNo);
                setDrawerTitle(row?.행사단체명 || '단체 상세');
                setDrawerOpen(true);
              }
            },
          })}
        />
      </Modal>

      {/* Tabs - Modern Design */}
      <div className="bg-white">
        {/* Stats Tab Content */}
        {activeTab === 'stats' && (
          <div className="pt-2 pb-4">
            <div className="px-6 pb-6">
              {/* Key Metrics - Modern Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">매출</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">총 상품금액</p>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{loading ? '...' : formatCurrency(totalSales)}</p>
                  <p className="text-xs text-gray-500">총 {filteredRows.length}건</p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">입금</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">총 입금액</p>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{loading ? '...' : formatCurrency(totalReceived)}</p>
                  <p className="text-xs text-gray-500">입금 기준</p>
                </div>
                
                <div
                  className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    if (!outstandingLoading && Array.isArray(outstandingRows) && outstandingRows.length > 0) {
                      setOutstandingModalOpen(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">미수</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">미수금</p>
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {loading || outstandingLoading ? '...' : formatCurrency(totalOutstandingReceivable || totalReceivable)}
                  </p>
                  <p className="text-xs text-gray-500">미수현황 기준</p>
                </div>
                
                <div
                  className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    if (Array.isArray(groupedSettleRows) && groupedSettleRows.length > 0) {
                      setPreDepositModalOpen(true);
                    }
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">전도금</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">전도금 합계</p>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalPreDepositFromGroups)}</p>
                  <p className="text-xs text-gray-500">단체 기준</p>
                </div>
                
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                    <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-full">수익</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">예상수익 합계</p>
                  <p className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(totalExpectedProfitFromRows)}</p>
                  <p className="text-xs text-gray-500">전체 예상</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Group Tab Content */}
        {activeTab === 'group' && (
          <div className="pt-2 pb-4">
            <div className="px-6">
              {settleLoading && <div className="mt-2 text-sm text-gray-500">단체 결산 데이터를 불러오는 중...</div>}
              {!settleLoading && settleError && <div className="mt-2 text-sm text-red-600">{settleError}</div>}
            </div>
            <div className="px-0 pb-6">
              <div className="bg-white rounded-xl overflow-hidden border-y border-gray-100">
                <div className="overflow-x-auto reservation-table settlement-reservation-table">
                  {groupHeaderFilterPopover}
                  <Table
                    size="middle"
                    pagination={false}
                    tableLayout="fixed"
                    scroll={{ x: groupTotalTableWidth }}
                    sticky={{ offsetHeader: 0 }}
                    rowKey={(row, idx) => String(row?.행사단체번호 ?? row?.대표판매번호 ?? row?.대표주문번호 ?? idx)}
                    rowClassName={(_, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                    columns={groupTableColumns}
                    dataSource={Array.isArray(groupSortedRows) ? groupSortedRows : []}
                    onRow={(row) => ({
                      onClick: () => openGroupDrawer(row),
                      style: { cursor: 'pointer' },
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Orders Tab Content */}
        {activeTab === 'orders' && (
          <div className="pt-2 pb-4">
            <div className="px-0 pb-6">
              <div className="bg-white rounded-xl overflow-hidden border-y border-gray-100">
                <div className="px-4 pt-3 pb-2 border-b border-gray-100 flex justify-end bg-white">
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-600">
                    <div>
                      <span className="mr-1 font-medium">인원</span>
                      <span className="font-mono tabular-nums">{totalQuantity.toLocaleString('ko-KR')}</span>
                    </div>
                    <div>
                      <span className="mr-1 font-medium">상품금액</span>
                      <span className="font-mono tabular-nums">{formatCurrency(totalSales)}</span>
                    </div>
                    <div>
                      <span className="mr-1 font-medium">입금액</span>
                      <span className="font-mono tabular-nums">{formatCurrency(totalReceived)}</span>
                    </div>
                    <div>
                      <span className="mr-1 font-medium">미수액</span>
                      <span className="font-mono tabular-nums">{formatCurrency(totalReceivable)}</span>
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto reservation-table settlement-reservation-table">
                  {orderHeaderFilterPopover}
                  <Table
                    size="middle"
                    pagination={false}
                    tableLayout="fixed"
                    loading={loading}
                    scroll={{ x: ordersTotalTableWidth }}
                    sticky={{ offsetHeader: 0 }}
                    rowKey={(row, idx) => String(row?.주문번호 ?? row?.orderNo ?? idx)}
                    rowClassName={(_, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                    columns={orderTableColumns}
                    dataSource={Array.isArray(ordersSortedRows) ? ordersSortedRows : []}
                    onRow={(row) => ({
                      onClick: () => openReservationDetailFromOrder(row),
                      onMouseEnter: (e) => handleOrderRowMouseEnter(e, row),
                      onMouseMove: (e) => handleOrderRowMouseMove(e, row),
                      onMouseLeave: () => handleOrderRowMouseLeave(),
                      style: { cursor: 'pointer' },
                    })}
                  />
                </div>
                {!loading && error && (
                  <div className="px-6 py-3 text-sm text-red-600 border-t border-gray-100">{error}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reservationSearch' && (
          <div className="pt-0 pb-2 px-0">
            <ReservationManagement embedded hideTabs fixedMode="reservations" />
          </div>
        )}

        {/* Product Tab Content */}
        {activeTab === 'product' && (
          <div className="pt-2 pb-4">
            <div className="overflow-x-auto px-0 pb-6">
              <div className="bg-white rounded-xl overflow-hidden border-y border-gray-100">
                <table className="min-w-full">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">종류</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">상품명</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">건수(취소)</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">총 판매가</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">총 입금액</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">총 미수액</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">완료건</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 whitespace-nowrap">부분건</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {groupedProductRows.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-sm text-gray-500">
                          <div className="flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            표시할 데이터가 없습니다.
                          </div>
                        </td>
                      </tr>
                    )}
                    {groupedProductRows.map((row, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {row.종류}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            className="text-sm font-medium text-gray-600 hover:text-blue-800 hover:underline text-left"
                            onClick={() => {
                              const target = (row?.상품번호 != null && String(row.상품번호).trim() !== '')
                                ? String(row.상품번호)
                                : String(row?.상품코드 || '').trim();

                              if (!target) return;

                              navigate(`/orders/${encodeURIComponent(target)}`,
                                {
                                  state: {
                                    productName: row?.상품명 || '',
                                    productNo: row?.상품번호 ?? null,
                                  },
                                }
                              );
                            }}
                          >
                            {row.상품명}
                          </button>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-700 text-sm font-semibold">
                            {row.취소완료건 > 0 ? `${row.건수}(${row.취소완료건})` : row.건수}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center font-mono">{formatCurrency(row.총판매가)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center font-mono">{formatCurrency(row.총입금액)}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            row.총미수액 > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                          }`}>
                            {formatCurrency(row.총미수액)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-700 text-sm font-semibold">
                            {row.완료건}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-50 text-gray-700 text-sm font-semibold">
                            {row.부분건}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Outstanding Receivables Modal */}
      <Modal
        open={outstandingModalOpen}
        onCancel={() => setOutstandingModalOpen(false)}
        footer={null}
        width={1200}
        title={
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500" />
            <span className="font-semibold text-gray-900">미수 상세 내역</span>
          </div>
        }
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {outstandingError && (
          <div className="mb-2 text-xs text-red-600">{outstandingError}</div>
        )}
        <Table
          size="small"
          loading={outstandingLoading}
          className="whitespace-nowrap"
          rowKey={(row, idx) => String(row?.판매번호 ?? row?.주문번호 ?? idx)}
          pagination={false}
          dataSource={Array.isArray(outstandingRows) ? outstandingRows : []}
          onRow={(row) => ({
            onClick: () => {
              const saleNo = row?.판매번호;
              if (!saleNo) return;
              setDrawerSaleNo(saleNo);
              setDrawerTitle(row?.행사단체명 || '단체정보');
              setOutstandingModalOpen(false);
              setDrawerOpen(true);
            },
          })}
          columns={[
            {
              title: '행사일시',
              dataIndex: '행사일시',
              key: '행사일시',
              align: 'center',
              width: 130,
            },
            {
              title: '행사단체명',
              dataIndex: '행사단체명',
              key: '행사단체명',
              align: 'left',
              width: 260,
            },
            {
              title: '예약담당자',
              dataIndex: '예약담당자',
              key: '예약담당자',
              align: 'center',
              width: 120,
            },
            {
              title: '예약상태명',
              dataIndex: '예약상태명',
              key: '예약상태명',
              align: 'center',
              width: 120,
            },
            {
              title: '미수금액',
              dataIndex: '미수금액',
              key: '미수금액',
              align: 'right',
              width: 140,
              render: (v) => <span className="tabular-nums">{formatCurrency(v)}</span>,
            },
          ]}
          scroll={{ x: 'max-content', y: 420 }}
        />
      </Modal>

      {/* Group Info Drawer */}
      <GroupInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        saleNo={drawerSaleNo}
        title={drawerTitle}
      />
    </div>
  );
};

export default SettlementSystem;
